import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { createChecklistNameSchema } from '../schemas';
import type { Checklist } from '../types';

interface ChecklistCardHeaderProps {
  checklist: Checklist;
  collapsed: boolean;
  /** Completion percentage (0–100) computed by the parent. */
  progress: number;
  onToggleCollapsed: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}

/** Header row of a checklist card: collapse toggle, editable name, progress, menu. */
export function ChecklistCardHeader({
  checklist,
  collapsed,
  progress,
  onToggleCollapsed,
  onRename,
  onDelete,
}: ChecklistCardHeaderProps) {
  const { t } = useTranslation('checklists');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(checklist.name);

  function commitName() {
    const parsed = createChecklistNameSchema(t).safeParse(nameDraft);
    if (parsed.success && parsed.data !== checklist.name) onRename(parsed.data);
    else setNameDraft(checklist.name);
    setEditingName(false);
  }

  return (
    <div className="group/card flex items-center gap-1.5 px-1 py-1">
      <button
        type="button"
        onClick={onToggleCollapsed}
        className="text-muted-foreground transition-transform hover:text-foreground"
        style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
        aria-expanded={!collapsed}
        aria-label={t('checklist.toggle')}
        data-testid="checklists-card-toggle"
      >
        <ChevronRight className="size-4" />
      </button>

      {editingName ? (
        <Input
          autoFocus
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitName();
            else if (e.key === 'Escape') {
              setNameDraft(checklist.name);
              setEditingName(false);
            }
          }}
          className="h-7 flex-1 text-sm font-medium"
          data-testid="checklists-card-name-input"
        />
      ) : (
        <span className="truncate text-sm font-medium">
          {checklist.name || t('checklist.untitled')}
        </span>
      )}

      {!editingName && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 opacity-0 transition-opacity group-hover/card:opacity-100"
              onClick={() => {
                setNameDraft(checklist.name);
                setEditingName(true);
              }}
              aria-label={t('checklist.rename')}
              data-testid="checklists-card-edit"
            >
              <Pencil className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('checklist.rename')}</TooltipContent>
        </Tooltip>
      )}

      <div className="ml-1 h-1 w-24 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${progress}%` }}
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      <span className="shrink-0 text-xs text-muted-foreground">
        {t('checklist.progress', { done: checklist.checkedItems, total: checklist.totalItems })}
      </span>

      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="ml-auto h-6 w-6 opacity-0 transition-opacity group-hover/card:opacity-100 data-[state=open]:opacity-100"
                aria-label={t('checklist.openMenu')}
                data-testid="checklists-card-menu"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('checklist.openMenu')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={onDelete}
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <Trash2 className="size-4" />
            {t('checklist.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
